import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchTreatmentList } from '../store/treatmentListSlice'
import { fetchVeterinarians } from '../store/settingsSlice'
import { TreatmentListTable } from '../components/treatments/TreatmentListTable'
import { TreatmentSearch } from '../components/treatments/TreatmentSearch'
import { NewTreatmentModal } from '../components/treatments/NewTreatmentModal'
import { Plus } from 'lucide-react'

export function TreatmentsPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { treatments, loading } = useAppSelector((s) => s.treatmentList)

  const [searchQuery, setSearchQuery] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchTreatmentList())
    dispatch(fetchVeterinarians())
  }, [dispatch])

  const filteredTreatments = useMemo(() => {
    return treatments.filter((tx) => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const txNumber = `TX-${String(tx.treatment_number).padStart(4, '0')}`.toLowerCase()
        if (
          !tx.patient_name.toLowerCase().includes(q) &&
          !txNumber.includes(q) &&
          !(tx.diagnosis || '').toLowerCase().includes(q)
        ) {
          return false
        }
      }
      // Type filter
      if (typeFilter && tx.treatment_type !== typeFilter) {
        return false
      }
      // Status filter
      if (statusFilter && tx.status !== statusFilter) {
        return false
      }
      return true
    })
  }, [treatments, searchQuery, typeFilter, statusFilter])

  if (loading) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="treatments-page">
        <p className="text-[13px] text-text-muted">Loading treatments...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="treatments-page">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[16px] font-semibold text-text-primary">Treatments</h1>
        <button
          data-testid="new-treatment-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          <Plus size={14} />
          New Treatment
        </button>
      </div>

      {/* Search and filters */}
      <div className="mb-4">
        <TreatmentSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <TreatmentListTable treatments={filteredTreatments} />
      </div>

      {/* New Treatment Modal */}
      <NewTreatmentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={(treatment) => {
          setModalOpen(false)
          dispatch(fetchTreatmentList())
          navigate(`/treatments/${treatment.id}`)
        }}
      />
    </div>
  )
}
